import { __, sprintf } from '@wordpress/i18n';
import {
    useEffect,
    useState,
    useCallback,
    useMemo,
    useRef,
} from '@wordpress/element';
import { decodeEntities } from '@wordpress/html-entities';
import apiFetch from '@wordpress/api-fetch';
import { addQueryArgs } from '@wordpress/url';
import { dateI18n, getSettings } from '@wordpress/date';
import {
    AdminDataViews as DataViews,
    DokanTooltip as Tooltip,
    DokanModal,
    DateRangePicker,
    DokanLink,
} from '@dokan/components';
import { truncate } from '@dokan/utilities';
import { SimpleInput, SearchableSelect, ReactSelect } from '@getdokan/dokan-ui';
import { Calendar, User, Users } from 'lucide-react';
import StatusPill from '../components/quote-list/StatusPill';
import HeaderSection from '../components/common/HeaderSection';

declare const moment: any;

const QUOTE_TABS = [
    { name: 'all', label: __( 'All', 'dokan' ) },
    { name: 'pending', label: __( 'Pending', 'dokan' ) },
    { name: 'updated', label: __( 'Updated', 'dokan' ) },
    { name: 'accepted', label: __( 'Accepted', 'dokan' ) },
    { name: 'cancel', label: __( 'Cancelled', 'dokan' ) },
    { name: 'approve', label: __( 'Approved', 'dokan' ) },
    { name: 'expired', label: __( 'Expired', 'dokan' ) },
    { name: 'draft', label: __( 'Draft', 'dokan' ) },
    { name: 'trash', label: __( 'Trash', 'dokan' ) },
    { name: 'reject', label: __( 'Rejected', 'dokan' ) },
    { name: 'converted', label: __( 'Converted', 'dokan' ) },
];

const RequestQuoteList = ( props ) => {
    const [ data, setData ] = useState< any[] >( [] );
    const [ isLoading, setIsLoading ] = useState< boolean >( false );
    // Match Dokan Admin DataViews expected view shape
    const defaultLayouts = {
        table: { density: 'comfortable' },
        grid: {},
        list: {},
    };

    const [ selection, setSelection ] = useState< any[] >( [] );
    const [ totalItems, setTotalItems ] = useState< number >( 0 );
    const [ activeTab, setActiveTab ] = useState< string >( 'all' );
    const [ statusCounts, setStatusCounts ] = useState( {
        all: 0,
        pending: 0,
        approve: 0,
        cancel: 0,
        draft: 0,
        trash: 0,
        updated: 0,
        accepted: 0,
        reject: 0,
        expired: 0,
        converted: 0,
    } );

    // Filters state (follow withdraw page patterns)
    const [ customer, setCustomer ] = useState< any >( null );
    const [ after, setAfter ] = useState( '' );
    const [ afterText, setAfterText ] = useState( '' );
    const [ before, setBefore ] = useState( '' );
    const [ beforeText, setBeforeText ] = useState( '' );
    const [ focusInput, setFocusInput ] = useState( 'startDate' );
    const [ showDatePicker, setShowDatePicker ] = useState( false );

    // Applied filter args — only updated on explicit user action (e.g. OK click).
    const [ filterArgs, setFilterArgs ] = useState< Record< string, any > >(
        {}
    );

    // Confirmation modal state
    const [ confirmModal, setConfirmModal ] = useState< {
        isOpen: boolean;
        title: string;
        description: string;
        onConfirm: () => void;
    } >( { isOpen: false, title: '', description: '', onConfirm: () => {} } );

    const fields = [
        {
            id: 'quote',
            label: __( 'Quote', 'dokan' ),
            render: ( { item } ) => {
                const quoteLabel =
                    item?.user_id && item?.quote_title
                        ? item.quote_title
                        : sprintf(
                              /* translators: %s: Quote number */
                              __( 'Quote #%s', 'dokan' ),
                              item?.id
                          );
                return (
                    <div className="flex flex-col">
                        <DokanLink
                            as="div"
                            onClick={ () =>
                                props.navigate(
                                    `/request-for-quote/edit-quote/${ item.id }`
                                )
                            }
                            className="cursor-pointer space-y-1"
                        >
                            <span className="font-medium text-[#7047EB]">
                                { quoteLabel }
                            </span>
                            { item?.created_at && (
                                <span className="block text-xs text-gray-500">
                                    { item.created_at }
                                </span>
                            ) }
                        </DokanLink>
                    </div>
                );
            },
        },
        {
            id: 'customer',
            label: __( 'Customer', 'dokan' ),
            render: ( { item } ) => {
                return item?.customer_name ? (
                    <div className="flex items-center gap-2 min-w-48">
                        <div className="flex flex-col">
                            { item?.customer_name?.length <= 22 ? (
                                <span className="text-gray-900 font-medium">
                                    { decodeEntities( item?.customer_name ) }
                                </span>
                            ) : (
                                <Tooltip
                                    content={ decodeEntities(
                                        item?.customer_name
                                    ) }
                                >
                                    <span className="text-gray-900 font-medium">
                                        { decodeEntities(
                                            truncate( item?.customer_name, 22 )
                                        ) }
                                    </span>
                                </Tooltip>
                            ) }
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center gap-2 min-w-48">
                        <span className="text-sm text-gray-900">
                            { __( 'Guest', 'dokan' ) }
                        </span>
                    </div>
                );
            },
        },
        {
            id: 'status',
            label: __( 'Status', 'dokan' ),
            render: ( { item } ) => (
                <StatusPill
                    value={ ( item?.status || 'pending' ).toLowerCase() }
                />
            ),
        },
        {
            id: 'vendor',
            label: __( 'Vendor', 'dokan' ),
            render: ( { item } ) => {
                return item?.store_name ? (
                    <div className="flex items-center gap-2 min-w-48">
                        { item?.store_name?.length <= 22 ? (
                            <DokanLink
                                as="a"
                                onClick={ () =>
                                    props.navigate( `/vendors/${ item.id }` )
                                }
                                className="cursor-pointer"
                            >
                                <span className="text-[#7047EB] font-medium">
                                    { decodeEntities( item?.store_name ) }
                                </span>
                            </DokanLink>
                        ) : (
                            <DokanLink
                                as="a"
                                onClick={ () =>
                                    props.navigate( `/vendors/${ item.id }` )
                                }
                                className="cursor-pointer"
                            >
                                <Tooltip
                                    content={ decodeEntities(
                                        item?.store_name
                                    ) }
                                >
                                    <span className="text-[#7047EB] font-medium">
                                        { decodeEntities(
                                            truncate( item?.store_name, 22 )
                                        ) }
                                    </span>
                                </Tooltip>
                            </DokanLink>
                        ) }
                    </div>
                ) : (
                    <div className="flex items-center gap-2 min-w-48">
                        { __( 'N/A', 'dokan' ) }
                    </div>
                );
            },
        },
    ];

    const [ view, setView ] = useState( {
        perPage: 10,
        page: 1,
        search: '',
        type: 'table',
        status: 'all',
        layout: { density: 'comfortable' },
        titleField: 'quote',
        fields: fields.map( ( f ) =>
            [ 'customer', 'status', 'vendor' ].includes( f.id ) ? f.id : null
        ),
    } );

    const fetchData = useCallback( async () => {
        setIsLoading( true );
        try {
            const queryArgs: Record< string, any > = {
                per_page: view.perPage,
                page: view.page,
                status: activeTab !== 'all' ? activeTab : undefined,
                ...filterArgs,
            };

            const response: any = await apiFetch( {
                path: addQueryArgs( 'dokan/v1/request-for-quote', queryArgs ),
                parse: false,
            } );

            const items = await response.json();
            setData( Array.isArray( items ) ? items : [] );

            // Extract status counts from response headers
            const pendingCount = parseInt(
                response.headers.get( 'X-Status-Pending' ) || '0',
                10
            );
            const approveCount = parseInt(
                response.headers.get( 'X-Status-Approved' ) || '0',
                10
            );
            const cancelCount = parseInt(
                response.headers.get( 'X-Status-Cancelled' ) || '0',
                10
            );
            const draftCount = parseInt(
                response.headers.get( 'X-Status-Draft' ) || '0',
                10
            );
            const trashCount = parseInt(
                response.headers.get( 'X-Status-Trash' ) || '0',
                10
            );
            const updatedCount = parseInt(
                response.headers.get( 'X-Status-Updated' ) || '0',
                10
            );
            const acceptedCount = parseInt(
                response.headers.get( 'X-Status-Accepted' ) || '0',
                10
            );
            const rejectCount = parseInt(
                response.headers.get( 'X-Status-Rejected' ) || '0',
                10
            );
            const expiredCount = parseInt(
                response.headers.get( 'X-Status-Expired' ) || '0',
                10
            );
            const convertedCount = parseInt(
                response.headers.get( 'X-Status-Converted' ) || '0',
                10
            );

            // "All" = sum of all statuses except trash
            const allCount =
                pendingCount +
                approveCount +
                cancelCount +
                draftCount +
                updatedCount +
                acceptedCount +
                rejectCount +
                expiredCount +
                convertedCount;

            const counts: Record< string, number > = {
                all: allCount,
                pending: pendingCount,
                approve: approveCount,
                cancel: cancelCount,
                draft: draftCount,
                trash: trashCount,
                updated: updatedCount,
                accepted: acceptedCount,
                reject: rejectCount,
                expired: expiredCount,
                converted: convertedCount,
            };
            setStatusCounts( counts as any );

            // Set totalItems based on the active tab's count for correct pagination
            setTotalItems( counts[ activeTab ] || 0 );
        } catch {
            setData( [] );
            setTotalItems( 0 );
        } finally {
            setIsLoading( false );
        }
    }, [ view.page, view.perPage, activeTab, filterArgs ] );

    useEffect( () => {
        fetchData();
    }, [ fetchData ] );

    const dateFormat = getSettings().formats.date;

    // Derive unique customer options from loaded quotes (same as Vue multiselect approach).
    // Keep a ref so options persist when data is filtered by a selected customer.
    const customerOptionsRef = useRef< { value: string; label: string }[] >(
        []
    );
    const customerOptions = useMemo( () => {
        // Only rebuild options when no customer filter is active;
        // otherwise the data is already filtered and would shrink the list.
        if ( ! filterArgs.customer ) {
            const seen = new Set< string >();
            const options = data.reduce< { value: string; label: string }[] >(
                ( acc, item ) => {
                    if (
                        item.customer_name &&
                        ! seen.has( item.customer_name )
                    ) {
                        seen.add( item.customer_name );
                        acc.push( {
                            value: item.customer_name,
                            label: item.customer_name,
                        } );
                    }
                    return acc;
                },
                []
            );
            customerOptionsRef.current = options;
            return options;
        }
        return customerOptionsRef.current;
    }, [ data, filterArgs.customer ] );

    const displayDateRange = ( startDate: string, endDate: string ) => {
        return `${ dateI18n( dateFormat, startDate ) } - ${ dateI18n(
            dateFormat,
            endDate
        ) }`;
    };

    const componentsContainer = {
        ValueContainer: ( { children, ...restProps }: any ) => (
            <ReactSelect.components.ValueContainer { ...restProps }>
                <div className="flex items-center">
                    <span className="inline-flex h-full items-center pl-2 pr-3">
                        <Users size={ 16 } className="text-[#4F4F4F]" />
                    </span>
                    { children }
                </div>
            </ReactSelect.components.ValueContainer>
        ),
    };

    const filterFields = () => [
        {
            id: 'date',
            label: __( 'Date', 'dokan' ),
            field: (
                <DateRangePicker
                    show={ showDatePicker }
                    setShow={ setShowDatePicker }
                    after={ after }
                    afterText={ afterText }
                    before={ before }
                    beforeText={ beforeText }
                    onUpdate={ ( update: any ) => {
                        if ( update.after ) {
                            setAfter( update.after );
                        }
                        if ( update.afterText ) {
                            setAfterText( update.afterText );
                        }
                        if ( update.before ) {
                            setBefore( update.before );
                        }
                        if ( update.beforeText ) {
                            setBeforeText( update.beforeText );
                        }
                        if ( update.focusedInput ) {
                            setFocusInput( update.focusedInput );
                            if ( update.focusedInput === 'endDate' && after ) {
                                setBefore( '' );
                                setBeforeText( '' );
                            }
                        }
                    } }
                    shortDateFormat="MM/DD/YYYY"
                    focusedInput={ focusInput }
                    isInvalidDate={ ( date: string ) =>
                        moment().isBefore( moment( date ), 'date' )
                    }
                    wrapperClassName="w-full"
                    pickerToggleClassName="block"
                    wpPopoverClassName="dokan-layout"
                    popoverBodyClassName="p-4 w-auto text-sm/6"
                    onClear={ () => {
                        setAfter( '' );
                        setAfterText( '' );
                        setBefore( '' );
                        setBeforeText( '' );
                        const args = { ...filterArgs };
                        delete args[ 'date[from_date]' ];
                        delete args[ 'date[to_date]' ];
                        setFilterArgs( args );
                    } }
                    onOk={ () => {
                        const args = { ...filterArgs };
                        if ( after ) {
                            args[ 'date[from_date]' ] = dateI18n(
                                'Y-m-d',
                                after
                            );
                        }
                        if ( before ) {
                            args[ 'date[to_date]' ] = dateI18n(
                                'Y-m-d',
                                before
                            );
                        }
                        setFilterArgs( args );
                    } }
                >
                    <SimpleInput
                        addOnLeft={ <Calendar size="16" /> }
                        className="border rounded px-3 py-1.5 w-full bg-white"
                        onChange={ () => {} }
                        input={ {
                            type: 'text',
                            value:
                                ! after || ! before
                                    ? ''
                                    : displayDateRange( after, before ),
                            placeholder: __( 'Date', 'dokan' ),
                            readOnly: true,
                        } }
                    />
                </DateRangePicker>
            ),
        },
        {
            id: 'customer',
            label: __( 'Customer', 'dokan' ),
            field: (
                <SearchableSelect
                    key="customer-select"
                    value={ customer }
                    options={ customerOptions }
                    components={ { ...componentsContainer } }
                    onChange={ ( selectedCustomer: any ) => {
                        const args = { ...filterArgs };
                        delete args.customer;
                        if ( selectedCustomer ) {
                            args.customer = selectedCustomer.value;
                        }
                        setCustomer( selectedCustomer );
                        setFilterArgs( args );
                    } }
                    placeholder={ __( 'Filter by customer', 'dokan' ) }
                />
            ),
        },
    ];

    const tabs = useMemo(
        () =>
            QUOTE_TABS.map( ( t ) => ( {
                id: t.name,
                name: t.name,
                icon: (
                    <div className="flex items-center gap-1.5 px-2">
                        { t.label }
                        <span className="text-xs font-light text-[#A5A5AA]">
                            ({ ( statusCounts as any )[ t.name ] || 0 })
                        </span>
                    </div>
                ),
                title: t.label,
            } ) ),
        [ statusCounts ]
    );

    return (
        <div className="dokan-rfq-admin-quote-list">
            <div className="mb-6">
                <h2 className="text-2xl font-semibold text-gray-900">
                    { __( 'Request for Quotation', 'dokan' ) }
                </h2>
            </div>

            <HeaderSection active="quotes" />

            <DataViews
                data={ data }
                namespace="rfq-quote-list"
                defaultLayouts={ defaultLayouts }
                fields={ fields }
                getItemId={ ( item: any ) => item?.id || item?.quote_id }
                onChangeView={ setView as any }
                paginationInfo={ {
                    totalItems,
                    totalPages: Math.ceil( totalItems / view.perPage ),
                } }
                view={ view as any }
                selection={ selection }
                onChangeSelection={ setSelection as any }
                isLoading={ isLoading }
                tabs={ {
                    tabs,
                    onSelect: ( tabName: string ) => {
                        setActiveTab( tabName );
                        setView( ( prev: any ) => ( { ...prev, page: 1 } ) );
                    },
                    initialTabName: activeTab,
                } }
                actions={ [
                    {
                        id: 'pending',
                        label: () => __( 'Mark Pending', 'dokan' ),
                        supportsBulk: true,
                        isEligible: ( item: any ) => item.status !== 'pending',
                        callback: async ( items: any[] ) => {
                            const ids = items.map(
                                ( i ) => i.id || i.quote_id
                            );
                            if ( ! ids.length ) return;
                            await apiFetch( {
                                path: 'dokan/v1/request-for-quote/batch',
                                method: 'POST',
                                data: { action: 'pending', items: ids },
                            } ).catch( () => {} );
                            setSelection( [] );
                            fetchData();
                        },
                    },
                    {
                        id: 'trash',
                        label: () => __( 'Trash', 'dokan' ),
                        supportsBulk: true,
                        isEligible: ( item: any ) => item.status !== 'trash',
                        callback: ( items: any[] ) => {
                            const ids = items.map(
                                ( i ) => i.id || i.quote_id
                            );
                            if ( ! ids.length ) return;
                            setConfirmModal( {
                                isOpen: true,
                                title: __(
                                    'Trash Confirmation',
                                    'dokan'
                                ),
                                description: __(
                                    'Move selected quotes to Trash?',
                                    'dokan'
                                ),
                                onConfirm: async () => {
                                    await apiFetch( {
                                        path: 'dokan/v1/request-for-quote/batch',
                                        method: 'POST',
                                        data: {
                                            action: 'trash',
                                            items: ids,
                                        },
                                    } ).catch( () => {} );
                                    setSelection( [] );
                                    fetchData();
                                },
                            } );
                        },
                    },
                    {
                        id: 'delete',
                        label: () => __( 'Delete Permanently', 'dokan' ),
                        supportsBulk: true,
                        isEligible: ( item: any ) => item.status === 'trash',
                        callback: ( items: any[] ) => {
                            const ids = items.map(
                                ( i ) => i.id || i.quote_id
                            );
                            if ( ! ids.length ) return;
                            setConfirmModal( {
                                isOpen: true,
                                title: __(
                                    'Delete Confirmation',
                                    'dokan'
                                ),
                                description: __(
                                    'Delete selected quotes permanently?',
                                    'dokan'
                                ),
                                onConfirm: async () => {
                                    await apiFetch( {
                                        path: 'dokan/v1/request-for-quote/batch',
                                        method: 'POST',
                                        data: {
                                            action: 'delete',
                                            items: ids,
                                        },
                                    } ).catch( () => {} );
                                    setSelection( [] );
                                    fetchData();
                                },
                            } );
                        },
                    },
                ] }
                filter={ {
                    fields: filterFields(),
                    onFilterRemove: ( filterId: string ) => {
                        if ( filterId === 'customer' ) {
                            setCustomer( null );
                            const args = { ...filterArgs };
                            delete args.customer;
                            setFilterArgs( args );
                        }
                        if ( filterId === 'date' ) {
                            setAfter( '' );
                            setAfterText( '' );
                            setBefore( '' );
                            setBeforeText( '' );
                            const args = { ...filterArgs };
                            delete args[ 'date[from_date]' ];
                            delete args[ 'date[to_date]' ];
                            setFilterArgs( args );
                        }
                    },
                    onReset: () => {
                        setCustomer( null );
                        setAfter( '' );
                        setAfterText( '' );
                        setBefore( '' );
                        setBeforeText( '' );
                        setFilterArgs( {} );
                    },
                } }
            />

            <DokanModal
                isOpen={ confirmModal.isOpen }
                namespace="rfq-quote-list-confirm"
                onClose={ () =>
                    setConfirmModal( ( prev ) => ( {
                        ...prev,
                        isOpen: false,
                    } ) )
                }
                onConfirm={ confirmModal.onConfirm }
                dialogTitle={ confirmModal.title }
                confirmationTitle={ confirmModal.title }
                confirmationDescription={ confirmModal.description }
                confirmButtonText={ __( 'Confirm', 'dokan' ) }
                cancelButtonText={ __( 'Cancel', 'dokan' ) }
            />
        </div>
    );
};

export default RequestQuoteList;
